import { Component, OnInit } from '@angular/core';
import { forkJoin } from 'rxjs';
import { UsageAmountsService } from '../services/usage/usage-amounts.service';
import {
  PlatformMetricsMonthly,
  StorageTenantMonthlyPoint,
  StorageTenantSeries,
  TenantMetricBreakdown,
  TenantMonthlyUsage,
  TenantMonthlyUsagePoint,
} from '../app.types';
import { convertDateToMonth, convertBytesToUnit, formatBytesHuman, scaleBytesToStorageUnit, StorageUnit, unitToBytes } from 'src/util/conversion-helpers';
import { buildTopNStack, TenantSeriesValues, TenantStackSeries } from 'src/util/tenant-stack';

interface RangeOption {
  label: string;
  months: number;
  /** When set, show only the in-progress current month; otherwise the N completed months before it. */
  currentOnly?: boolean;
}

/** One per-tenant stacked chart on the main page: a metric split by tenant, month by month. */
interface StackChart {
  id: string;
  /** Includes the unit where one applies, e.g. "Video Streamed (mins)". */
  title: string;
  unit: string;
  series: TenantStackSeries[];
  /** Tooltip value formatter; set for byte metrics so a tiny tenant reads in its own unit (e.g. MB). */
  formatValue?: (value: number) => string;
}

interface BreakdownRow {
  tenant: string;
  value: number;
  display: string;
}

/** A KPI metric that can be drilled into per tenant (the click-through totals table). */
interface BreakdownMetric {
  title: string;
  value: (t: TenantMetricBreakdown) => number;
  format: (n: number) => string;
}

/** How a usage metric's monthly per-tenant figure is read and labelled for its stacked chart. */
interface StackMetric {
  title: string;
  value: (p: TenantMonthlyUsagePoint) => number;
  unit: string;
  /** Clip data is a byte figure: scale the chart to GB/TB/PB rather than show a huge byte count. */
  isBytes?: boolean;
}

/**
 * Synectics-internal, cross-tenant platform metrics. Shows platform-wide monthly figures for a
 * selectable window (Synx-gated endpoint over already-aggregated blob + Usage DB data). Every metric is
 * charted per tenant as a stacked bar (top tenants + "Other") on the main page; each usage KPI tile can
 * also be clicked to read the exact per-tenant totals for the range in a table.
 */
@Component({
    selector: 'metrics-dashboard',
    templateUrl: './metrics-dashboard.component.html',
    styleUrls: ['./metrics-dashboard.component.scss'],
    standalone: false
})
export class MetricsDashboardComponent implements OnInit {
  readonly ranges: RangeOption[] = [
    { label: 'Current month', months: 1, currentOnly: true },
    { label: 'Last month', months: 1 },
    { label: 'Last 3 months', months: 3 },
    { label: 'Last 6 months', months: 6 },
    { label: 'Last 12 months', months: 12 },
  ];
  selectedRange: RangeOption = this.ranges[this.ranges.length - 1]; // default: Last 12 months

  isLoading = false;
  data: PlatformMetricsMonthly[] = [];

  currentMonthHint = '';

  // Headline figures (range totals; storage is a point-in-time value so we show the latest month).
  // The two storage figures auto-scale to GB/TB/PB so a large platform does not read as a huge GB count.
  incidentsTotal = 0;
  currentStorage: { value: number; unit: StorageUnit } = { value: 0, unit: 'GB' };
  videoMinutes = 0;
  redactionMinutes = 0;
  clipDataAdded: { value: number; unit: StorageUnit } = { value: 0, unit: 'GB' };
  clipsAddedTotal = 0;
  uniqueUsers = 0;

  // Shared month axis for every stacked chart, the featured storage chart, then the grid of the rest.
  stackCategories: string[] = [];
  featuredChart: StackChart | null = null;
  stackCharts: StackChart[] = [];

  // Per-tenant data behind the stacked charts.
  private storageByTenant: StorageTenantSeries[] = [];
  private tenantMonthly: TenantMonthlyUsage[] = [];

  // Per-tenant exact totals table (opened by clicking a KPI tile).
  breakdownOpen = false;
  breakdownLoading = false;
  breakdownTitle = '';
  breakdownRows: BreakdownRow[] = [];
  private breakdownMetricKey = '';
  private breakdownCache: TenantMetricBreakdown[] | null = null;

  // Guards against an earlier request's response overwriting a later selection's data.
  private requestSeq = 0;

  private static readonly monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

  // Drives which KPI tiles are clickable and how each tenant value is read/formatted in the totals table.
  private readonly breakdownMetrics: Record<string, BreakdownMetric> = {
    incidents: { title: 'Incidents Created', value: (t) => t.incidentsCreated, format: (n) => n.toLocaleString() },
    uniqueUsers: { title: 'Unique Users', value: (t) => t.uniqueUsers, format: (n) => n.toLocaleString() },
    video: { title: 'Video Streamed (mins)', value: (t) => Math.round(t.videoMinutesStreamed), format: (n) => n.toLocaleString() },
    redaction: { title: 'Redaction (mins)', value: (t) => Math.round(t.redactionSeconds / 60), format: (n) => n.toLocaleString() },
    // Sort/filter on the raw byte figure and format it into a human unit, so a tenant with well under a
    // gigabyte of clip data still appears (and reads as MB) instead of flooring to 0 GB and being
    // dropped - which otherwise left the table listing fewer tenants than the stacked chart.
    clipData: { title: 'Clip Data Added', value: (t) => t.clipDataAddedBytes, format: (n) => formatBytesHuman(n) },
    clipsAdded: { title: 'Clips Added', value: (t) => t.clipsAdded, format: (n) => n.toLocaleString() },
  };

  // The usage metrics charted per tenant on the main page, read per month per tenant. Clip data is a
  // byte figure (scaled to GB/TB/PB); the rest are counts or minutes shown as-is.
  private readonly stackMetrics: StackMetric[] = [
    { title: 'Incidents Created', value: (p) => p.incidentsCreated, unit: '' },
    { title: 'Unique Users per Month', value: (p) => p.uniqueLogins, unit: '' },
    { title: 'Video Streamed', value: (p) => Math.round(p.videoMinutesStreamed), unit: 'mins' },
    { title: 'Redaction', value: (p) => Math.round(p.redactionSeconds / 60), unit: 'mins' },
    { title: 'Clips Added', value: (p) => p.clipsAdded, unit: '' },
    { title: 'Clip Data Added', value: (p) => p.clipDataAddedBytes, unit: 'GB', isBytes: true },
  ];

  constructor(private usageService: UsageAmountsService) {}

  ngOnInit(): void {
    this.buildView();
    this.load();
  }

  /** Subtext shown on the KPI tiles, matching the selected range (e.g. "Last 3 months"). */
  get rangeLabel(): string {
    return this.selectedRange.label;
  }

  selectRange(range: RangeOption): void {
    if (range === this.selectedRange) {
      return;
    }
    this.selectedRange = range;
    this.load();
  }

  private load(): void {
    const seq = ++this.requestSeq;
    this.isLoading = true;
    this.breakdownCache = null; // range changed - the totals table must be re-fetched
    const range = this.selectedRange;

    // The main page now charts every metric per tenant, so the per-tenant monthly series is needed up
    // front alongside the platform series (the exact totals table stays a lazy fetch on card click).
    forkJoin({
      platform: this.usageService.getPlatformMetrics(range.months, !!range.currentOnly),
      monthly: this.usageService.getTenantMonthly(range.months, !!range.currentOnly),
    }).subscribe(({ platform, monthly }) => {
      if (seq !== this.requestSeq) {
        return; // a newer selection has superseded this response
      }
      this.data = platform && Array.isArray(platform.months) ? platform.months : [];
      this.uniqueUsers = platform?.uniqueUsersInRange ?? 0;
      this.storageByTenant = platform && Array.isArray(platform.storageByTenant) ? platform.storageByTenant : [];
      this.tenantMonthly = Array.isArray(monthly) ? monthly : [];
      this.buildView();
      this.isLoading = false;
      // The stacked charts measure their container width when created; they are revealed as data lands,
      // before layout settles, so nudge a re-measure once the browser has laid them out.
      setTimeout(() => window.dispatchEvent(new Event('resize')));
    });
  }

  private buildView(): void {
    const now = new Date();
    const hasCurrentMonth = this.data.some((d) => d.year === now.getFullYear() && d.month === now.getMonth() + 1);
    this.currentMonthHint = hasCurrentMonth
      ? `${MetricsDashboardComponent.monthNames[now.getMonth()]} ${now.getFullYear()} is the current month and still in progress.`
      : '';

    const sum = (selector: (d: PlatformMetricsMonthly) => number) =>
      this.data.reduce((acc, d) => acc + (selector(d) || 0), 0);

    const latestTotalStorage = this.data.length ? this.data[this.data.length - 1].totalStorageBytes : 0;

    this.incidentsTotal = sum((d) => d.incidentsCreated);
    this.currentStorage = scaleBytesToStorageUnit(latestTotalStorage);
    this.videoMinutes = Math.round(sum((d) => d.videoMinutesStreamed));
    this.redactionMinutes = Math.round(sum((d) => d.redactionSeconds) / 60);
    this.clipDataAdded = scaleBytesToStorageUnit(sum((d) => d.clipDataAddedBytes));
    this.clipsAddedTotal = sum((d) => d.clipsAdded);
    // uniqueUsers is set from the response (deduplicated across the range), not summed here.

    this.stackCategories = this.data.map((d) => convertDateToMonth(new Date(d.year, d.month - 1, 1)));

    // The two storage charts come from the same per-tenant series (so they share a tenant set, colours
    // and legend); the usage charts fold their own top-N per metric from the monthly series.
    const maxTotalStorage = this.data.reduce((max, d) => Math.max(max, d.totalStorageBytes || 0), 0);
    const maxStorageAdded = this.data.reduce((max, d) => Math.max(max, d.storageAddedBytes || 0), 0);

    this.featuredChart = this.buildStorageStackChart('storageTotal', 'Total Storage by Tenant', (p) => p.totalStorageBytes, maxTotalStorage);

    this.stackCharts = [
      this.buildStorageStackChart('storageAdded', 'Storage Added by Tenant', (p) => p.storageAddedBytes, maxStorageAdded),
      ...this.stackMetrics.map((metric, i) => this.buildUsageStackChart(`usage${i}`, metric)),
    ];
  }

  /** Builds a stacked storage chart from the backend's top-N + "Other" per-tenant series, reading one
   *  of its byte measures and scaling the whole chart to a single unit chosen from the platform max. */
  private buildStorageStackChart(
    id: string, title: string, bytesOf: (p: StorageTenantMonthlyPoint) => number, maxBytes: number): StackChart {
    const unit = scaleBytesToStorageUnit(maxBytes).unit;
    const key = (year: number, month: number) => `${year}-${month}`;

    // precision 2 for the drawn bars (so the axis and segments read cleanly in the chart unit); members
    // feed only the "Other" hover breakdown, where a tenant can be far smaller than the chart unit, so
    // those carry full precision and are formatted into their own human byte unit in the tooltip.
    const toSeries = (s: StorageTenantSeries, precision: number): TenantStackSeries => {
      const byMonth = new Map(s.monthly.map((p) => [key(p.year, p.month), bytesOf(p)]));
      return {
        name: s.tenantName,
        values: this.data.map((d) => convertBytesToUnit(byMonth.get(key(d.year, d.month)) ?? 0, unit, precision)),
      };
    };

    const series = this.storageByTenant.map((s) => ({
      ...toSeries(s, 2),
      // "Other" carries its folded-in tenants so the chart can break it down on hover. A normal tenant
      // series has no members.
      members: s.members?.map((m) => toSeries(m, 9)),
    }));

    return { id, title: `${title} (${unit})`, unit, series, formatValue: (v) => formatBytesHuman(unitToBytes(v, unit)) };
  }

  /** Folds the per-tenant monthly data into a top-N + "Other" stack for one usage metric, scaling clip
   *  data to a byte unit and leaving counts / minutes as they are. */
  private buildUsageStackChart(id: string, metric: StackMetric): StackChart {
    const tenants = this.tenantMonthly;
    const key = (year: number, month: number) => `${year}-${month}`;

    const monthTotal = (d: PlatformMetricsMonthly) =>
      tenants.reduce((sum, t) => {
        const point = t.monthly.find((p) => p.year === d.year && p.month === d.month);
        return sum + (point ? metric.value(point) : 0);
      }, 0);

    // Clip data is in bytes: pick one unit for the whole chart from its tallest month, like the storage
    // charts. Other metrics are counts / minutes and need no scaling.
    const byteUnit: StorageUnit | undefined = metric.isBytes
      ? scaleBytesToStorageUnit(this.data.reduce((max, d) => Math.max(max, monthTotal(d)), 0)).unit
      : undefined;
    const unit = byteUnit ?? metric.unit;

    // For a byte metric, convert at full precision so a tiny tenant is not floored to zero before it can
    // reach the "Other" breakdown; the drawn bars are rounded to the chart unit afterwards.
    const scale: (raw: number) => number = byteUnit
      ? (bytes) => convertBytesToUnit(bytes, byteUnit, 9)
      : (raw) => raw;

    const tenantValues: TenantSeriesValues[] = tenants.map((t) => {
      const byMonth = new Map(t.monthly.map((p) => [key(p.year, p.month), metric.value(p)]));
      return {
        name: t.tenantName,
        values: this.data.map((d) => scale(byMonth.get(key(d.year, d.month)) ?? 0)),
      };
    });

    const series = buildTopNStack(tenantValues, 8);
    if (byteUnit) {
      // Round only the drawn series for a clean axis; each series' members stay at full precision for the
      // tooltip so the "Other" breakdown keeps its smallest tenants and still sums to its header.
      for (const s of series) {
        s.values = s.values.map((v) => this.round(v));
      }
    }

    return {
      id,
      title: unit ? `${metric.title} (${unit})` : metric.title,
      unit,
      series,
      formatValue: byteUnit ? (v) => formatBytesHuman(unitToBytes(v, byteUnit)) : undefined,
    };
  }

  /** Opens the per-tenant exact totals table for a KPI, fetching the breakdown for the current range
   *  once, then reusing it as the metric changes. */
  openBreakdown(metricKey: string): void {
    if (!this.breakdownMetrics[metricKey]) {
      return;
    }
    this.breakdownMetricKey = metricKey;
    this.breakdownTitle = this.breakdownMetrics[metricKey].title;
    this.breakdownOpen = true;

    if (this.breakdownCache) {
      this.applyBreakdown();
      return;
    }

    this.breakdownLoading = true;
    const range = this.selectedRange;
    this.usageService.getTenantBreakdown(range.months, !!range.currentOnly).subscribe((rows) => {
      this.breakdownCache = Array.isArray(rows) ? rows : [];
      this.breakdownLoading = false;
      this.applyBreakdown();
    });
  }

  closeBreakdown(): void {
    this.breakdownOpen = false;
  }

  private applyBreakdown(): void {
    const metric = this.breakdownMetrics[this.breakdownMetricKey];
    this.breakdownRows = (this.breakdownCache || [])
      .map((t) => {
        const value = metric.value(t);
        return { tenant: t.tenantName, value, display: metric.format(value) };
      })
      .filter((row) => row.value > 0)
      .sort((a, b) => b.value - a.value);
  }

  private round(value: number): number {
    return Math.round(value * 100) / 100;
  }
}
